// src/components/dashboard/VacationWidget.js

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVacation } from '../../contexts/VacationContext';
import { useDepartment } from '../../contexts/DepartmentContext';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/dashboard-widgets.css';

const VacationWidget = () => {
  const { t } = useTranslation('vacation');
  const navigate = useNavigate();

  const {
    getCurrentWeekVacations,
    error
  } = useVacation();

  const { departments } = useDepartment();
  const { currentCompany } = useAuth();

  const companyId = currentCompany?.id;

  const [weekVacations, setWeekVacations] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [widgetLoading, setWidgetLoading] = useState(false);

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getCurrentWeekDates = () => {
    const today = new Date();
    const currentDay = today.getDay();
    const diff = currentDay === 0 ? -6 : 1 - currentDay;

    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    monday.setHours(0, 0, 0, 0);

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      weekDates.push(date);
    }

    return weekDates;
  };

  const weekDates = useMemo(() => getCurrentWeekDates(), []);

  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;

    const fetchWeekVacations = async () => {
      setWidgetLoading(true);
      try {
        const vacations = await getCurrentWeekVacations();
        if (!cancelled) {
          setWeekVacations(vacations || []);
        }
      } catch (err) {
        console.error('Error fetching week vacations:', err);
        if (!cancelled) {
          setWeekVacations([]);
        }
      } finally {
        if (!cancelled) {
          setWidgetLoading(false);
        }
      }
    };

    fetchWeekVacations();

    return () => {
      cancelled = true;
    };
  }, [companyId, getCurrentWeekVacations]);

  const departmentOptions = useMemo(() => {
    if (!departments || departments.length === 0) return [];
    return departments.map(dept => ({
      id: dept.id,
      name: dept.name
    }));
  }, [departments]);

  const filteredVacations = useMemo(() => {
    let filtered = [...weekVacations];

    if (departmentFilter && departmentFilter !== 'all') {
      filtered = filtered.filter(v => v.departmentId === departmentFilter);
    }

    if (searchFilter) {
      const searchLower = searchFilter.toLowerCase();
      filtered = filtered.filter(v =>
        v.staffName?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [weekVacations, departmentFilter, searchFilter]);

  const vacationsByDate = useMemo(() => {
    const grouped = {};

    weekDates.forEach(date => {
      const dateStr = formatDateLocal(date);
      grouped[dateStr] = [];
    });

    filteredVacations.forEach(vacation => {
      const dateStr = vacation.date;
      if (grouped[dateStr]) {
        grouped[dateStr].push(vacation);
      }
    });

    return grouped;
  }, [filteredVacations, weekDates]);

  const stats = useMemo(() => {
    const today = formatDateLocal(new Date());
    const todayVacations = filteredVacations.filter(v => v.date === today);

    return {
      todayCount: todayVacations.length,
      weekCount: filteredVacations.length
    };
  }, [filteredVacations]);

  const formatDayLabel = (date) => {
    const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const dayName = dayNames[date.getDay()];
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${dayName}, ${day}.${month}`;
  };

  const isToday = (date) => {
    const today = new Date();
    return formatDateLocal(date) === formatDateLocal(today);
  };

  const handleDayClick = (date) => {
    const dateStr = formatDateLocal(date);
    navigate(`/admin/team-vacation?date=${dateStr}`);
  };

  const handleViewAll = () => {
    navigate('/admin/team-vacation');
  };

  if (widgetLoading && weekVacations.length === 0) {
    return (
      <div className="vacation-widget">
        <div className="widget-header">
          <h3>{t('dashboard.widget.title')}</h3>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('messages.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vacation-widget">
        <div className="widget-header">
          <h3>{t('dashboard.widget.title')}</h3>
        </div>
        <div className="error-state">
          <p className="error-message">× {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vacation-widget">
      <div className="widget-header">
        <h3>{t('dashboard.widget.title')}</h3>
        <div className="stats-summary">
          <span className="stat vacation">
            <strong>{stats.todayCount}</strong> {t('today')}
          </span>
          <span className="stat vacation">
            <strong>{stats.weekCount}</strong> {t('thisWeek')}
          </span>
        </div>
      </div>

      <div className="widget-filters">
        <div className="filter-group">
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="department-filter"
          >
            <option value="all">
              {t('allDepartments')}
            </option>
            {departmentOptions.map(dept => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder={t('filters.searchByName')}
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="search-filter"
          />
        </div>
      </div>

      {weekVacations.length === 0 && !searchFilter && departmentFilter === 'all' ? (
        <div className="empty-state">
          <div className="empty-icon">○</div>
          <h4>{t('dashboard.widget.noVacations')}</h4>
          <p>{t('empty.noVacationsForPeriod')}</p>
        </div>
      ) : filteredVacations.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">∅</div>
          <h4>{t('noResults')}</h4>
          <p>{t('noMatchingFilters')}</p>
          <button
            onClick={() => {
              setDepartmentFilter('all');
              setSearchFilter('');
            }}
            className="clear-filters-btn"
          >
            {t('filters.clearFilters')}
          </button>
        </div>
      ) : (
        <div className="vacation-week-list">
          {weekDates.map((date, index) => {
            const dateStr = formatDateLocal(date);
            const dayVacations = vacationsByDate[dateStr] || [];
            const hasVacations = dayVacations.length > 0;
            const isTodayDate = isToday(date);

            return (
              <div
                key={index}
                className={`vacation-day-item ${hasVacations ? 'has-vacations' : ''} ${isTodayDate ? 'today' : ''}`}
                onClick={() => hasVacations && handleDayClick(date)}
                style={{ cursor: hasVacations ? 'pointer' : 'default' }}
              >
                <div className="day-header">
                  <span className="day-label">
                    {formatDayLabel(date)}
                    {isTodayDate && (
                      <span className="today-badge"> · {t('today')}</span>
                    )}
                  </span>
                  <span className="vacation-count">
                    {hasVacations ? (
                      <>
                        {dayVacations.length} {dayVacations.length === 1
                          ? t('staffMemberSingular')
                          : t('staffMemberPlural')}
                      </>
                    ) : (
                      t('noDays')
                    )}
                  </span>
                </div>

                {hasVacations && (
                  <div className="staff-list">
                    {dayVacations.slice(0, 3).map((vacation, idx) => (
                      <span key={idx} className="staff-name">
                        {vacation.staffName}
                      </span>
                    ))}
                    {dayVacations.length > 3 && (
                      <span className="more-staff">
                        +{dayVacations.length - 3} {t('moreStaff')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="widget-footer">
        <button
          onClick={handleViewAll}
          className="view-all-btn"
        >
          {t('viewAll')} →
        </button>
        {(departmentFilter !== 'all' || searchFilter) && filteredVacations.length > 0 && (
          <p className="summary-text">
            {t('showingSummary', { count: filteredVacations.length, total: weekVacations.length })}
            <button
              onClick={() => {
                setDepartmentFilter('all');
                setSearchFilter('');
              }}
              className="clear-filters-link"
            >
              {t('clearFiltersLink')}
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

export default VacationWidget;